import { Principal } from "@icp-sdk/core/principal";
import { isCanisterId, isMultiUserCanisterUser, userCanisterId } from "./userId";
const BASE32_ALPHABET = "abcdefghijklmnopqrstuvwxyz234567";
const MAX_SUBACCOUNT_HEX_LENGTH = 64;
export function encodeIcrcAccount({ owner, subaccount }) {
    if (subaccount === undefined) {
        return owner.toText();
    }
    const subaccountText = uint8ArrayToHexString(subaccount).replace(/^0+/, "");
    if (subaccountText.length === 0) {
        return owner.toText();
    }
    return `${owner.toText()}-${encodeCrc({ owner, subaccount })}.${subaccountText}`;
}
export function decodeIcrcAccount(accountString) {
    const [principalAndMaybeChecksum, subaccountHex, ...unexpected] = accountString.split(".");
    if (principalAndMaybeChecksum === undefined || principalAndMaybeChecksum.length === 0) {
        throw new Error("Invalid account. No string provided.");
    }
    if (unexpected.length > 0) {
        throw new Error("Invalid account string format. Expected at most one '.' separator.");
    }
    if (subaccountHex === undefined) {
        return { owner: Principal.fromText(accountString) };
    }
    const [checksum, ...rest] = principalAndMaybeChecksum.split("-").reverse();
    const principalText = rest.reverse().join("-");
    if (checksum === undefined || principalText.length === 0) {
        throw new Error("Invalid account. Invalid checksum.");
    }
    const account = {
        owner: Principal.fromText(principalText),
        subaccount: hexStringToUint8Array(subaccountHex.padStart(MAX_SUBACCOUNT_HEX_LENGTH, "0")),
    };
    if (encodeCrc(account) !== checksum) {
        throw new Error("Invalid account. Invalid checksum.");
    }
    return account;
}
function encodeCrc({ owner, subaccount }) {
    const checksum = bigEndianCrc32(new Uint8Array([...owner.toUint8Array(), ...subaccount]));
    return encodeBase32(checksum);
}
function bigEndianCrc32(bytes) {
    let crc = 0xffffffff;
    for (const byte of bytes) {
        crc ^= byte;
        for (let i = 0; i < 8; i++) {
            const mask = -(crc & 1);
            crc = (crc >>> 1) ^ (0xedb88320 & mask);
        }
    }
    const value = (crc ^ 0xffffffff) >>> 0;
    return new Uint8Array([
        (value >>> 24) & 0xff,
        (value >>> 16) & 0xff,
        (value >>> 8) & 0xff,
        value & 0xff,
    ]);
}
function encodeBase32(bytes) {
    let output = "";
    let value = 0;
    let bits = 0;
    for (const byte of bytes) {
        value = (value << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31];
            bits -= 5;
        }
    }
    if (bits > 0) {
        output += BASE32_ALPHABET[(value << (5 - bits)) & 31];
    }
    return output;
}
export function uint8ArrayToHexString(bytes) {
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
function hexStringToUint8Array(hex) {
    if (hex.length % 2 !== 0 || /[^0-9a-f]/i.test(hex)) {
        throw new Error("Invalid account. Invalid subaccount.");
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return bytes;
}
// Every ICRC-1 subaccount is exactly this long.
const SUBACCOUNT_LENGTH = 32;
// The ledger account holding a user's funds. Mirrors `impl From<UserIdAndPrincipal> for Account` in
// backend/libraries/types/src/user.rs: the id a user had before being migrated to a MultiUser
// canister was that of their User canister, whose account held their funds, while anyone else, such
// as a user in a MultiUser canister, holds their own funds in the account of the principal they sign
// in with. `principal` is only called for the latter, so an earlier id's wallet never needs it.
export function userWalletAccount(userId, principal) {
    const userIdPrincipal = Principal.fromText(userId);
    return {
        owner: isCanisterId(userIdPrincipal) ? userIdPrincipal : Principal.fromText(principal()),
    };
}
// Whether `address` is an account of the id of a user in a MultiUser canister, under any subaccount,
// as it is if the user id is entered as the address, rather than their wallet (see
// `userWalletAccount`). No one can sign as such an id, so anything sent there is lost. Mirrors
// `PendingCryptoTransaction::is_to_indexed_user_id` in backend/libraries/types/src/cryptocurrency.rs.
// An ICP account identifier is a hash, from which the owner can't be read, so it is never refused.
export function isAccountOfMultiUserCanisterUserId(address) {
    let owner;
    try {
        owner = decodeIcrcAccount(address).owner;
    }
    catch {
        return false;
    }
    return isMultiUserCanisterUser(owner.toText());
}
// The account a user's canister spends as when it pulls funds the user has approved via ICRC-2, so
// the spender the user has to approve. The MultiUser canister holding the user spends each user's
// approval under a subaccount derived from their principal, mirroring
// `ledger_utils::spender_subaccount`, so that it only ever spends a user's own approval.
export function userCanisterSpenderAccount(userId, principal) {
    return {
        owner: userCanisterId(userId),
        subaccount: spenderSubaccount(Principal.fromText(principal())),
    };
}
// The account a group or community spends as when it pulls a payment from one of its members: its
// own, given its canister id, under the subaccount derived from the member's principal, so that it
// only ever spends a member's own approval. Mirrors `ledger_utils::spender_subaccount`.
export function memberSpenderAccount(canisterId, principal) {
    return {
        owner: Principal.fromText(canisterId),
        subaccount: spenderSubaccount(Principal.fromText(principal())),
    };
}
// The account which pulls a payment the user makes from their wallet, so the spender their wallet
// has to approve. The user's canister pulls it, except that the group or community pulls a payment
// in a message or a tip in a group or channel, since the user's canister can't make the transfer
// for them. `chatId` is the chat such a payment is made in, and is left out for any other payment,
// eg. accepting a P2P swap, which the user's canister makes wherever it is.
export function paymentSpenderAccount(userId, chatId, principal) {
    if (chatId !== undefined && chatId.kind !== "direct_chat") {
        return memberSpenderAccount(chatId.kind === "channel" ? chatId.communityId : chatId.groupId, principal);
    }
    return userCanisterSpenderAccount(userId, principal);
}
// What a payment pulled from a wallet costs on top of the transfers it makes. A payment from the
// user's wallet, or from an external wallet, is pulled once the wallet has approved whatever pulls
// it (see `paymentSpenderAccount`), and the ledger charges its transfer fee for the approval.
export function walletApprovalFee(transferFee) {
    return transferFee;
}
// How long an approval made for a single payment stays spendable. The allowance is the whole of the
// access the spender is granted, so it should outlive the payment it is for and nothing more: an
// approval left standing is one which could be spent at any point later.
export const APPROVAL_VALIDITY_MS = 10 * 60 * 1000;
// Mirrors `ledger_utils::convert_to_subaccount`: the principal's length followed by its bytes.
export function spenderSubaccount(principal) {
    const bytes = principal.toUint8Array();
    const subaccount = new Uint8Array(SUBACCOUNT_LENGTH);
    subaccount[0] = bytes.length;
    subaccount.set(bytes, 1);
    return subaccount;
}
// The user whose wallet this ledger account might be, which is what lets the UI name the other side
// of a transaction. Only an account with the default subaccount can be a wallet (see
// `userWalletAccount`), and then it is the owner's: the id of the User canister a user had before
// being migrated to a MultiUser canister is the id they had then, and the principal of a user in a
// MultiUser canister is left for the caller to resolve, since it is not their user id. Undefined for
// any other subaccount.
//
// There is no equivalent for the ICP ledger's AccountIdentifier, which is a hash and so cannot be
// inverted.
export function icrcAccountToUserId({ owner, subaccount }) {
    if (subaccount !== undefined &&
        (subaccount.length !== SUBACCOUNT_LENGTH || !subaccount.every((b) => b === 0))) {
        return undefined;
    }
    return owner.toText();
}
